import React, { useContext, useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useNavigate } from "react-router-dom";
import { AppointmentContext } from "../context/AppointmentContext";
import Swal from "sweetalert2";
import { PulseLoader } from "react-spinners";
import { toast, ToastContainer } from "react-toastify";
import ExistingPatient from "./ExistingPatient";
import { AnimatePresence, motion } from "framer-motion";
import config from "../config";

// Helper to convert "DD-MM-YYYY" to "YYYY-MM-DD"
function toISODate(dateString) {
  if (!dateString) return "";
  const [dd, mm, yyyy] = dateString.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function Calendar({ onNext, onPrevious, onReset, doctor }) {
  const navigate = useNavigate();
  const { setSelectedDate, hospitalName } = useContext(AppointmentContext);
  const today = new Date();

  // State
  const [modalData, setModalData] = useState(null);
  const [timings, setTimings] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [datesWithDoctors, setDatesWithDoctors] = useState(new Set());
  const [datesWithoutDoctors, setDatesWithoutDoctors] = useState(new Set());
  const [holidayDates, setHolidayDates] = useState(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const naDates = ["2025-05-29", "2025-06-02"];
  const [selectedTimes, setSelectedTimes] = useState({});

  const futureDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i + 1);
    return date.toISOString().split("T")[0];
  });

  const allDatesInMonth = Array.from({ length: 30 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date.toISOString().split("T")[0];
  });

  // Fetch doctor availability
  const getDoctorWeeklySchedule = async () => {
    try {
      const availableDates = new Set();
      const unavailableDates = new Set();
      const holidays = new Map();

      const response = await fetch(`${config.API_BASE_URL}/GetDoctorWeeklySchedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dept: sessionStorage.getItem("SELECTED_DEPARTMENT_ID") || "",
          type: hospitalName === "hazra" ? "hazra" : "newtown",
        }),
      });

      const result = await response.json();
      const futureDatesSet = new Set(futureDates);

      if (result.status === 200 && result.data["weeklySchedule-details"]) {
        const scheduleList = result.data["weeklySchedule-details"];
        const dateAvailabilityMap = {};

        for (const entry of scheduleList) {
          const isoDate = toISODate(entry.WEEKDAY_DATE);
          if (!futureDatesSet.has(isoDate)) continue;

          const rawStatus = (entry.AVAILABLE_STATUS || "").trim();
          const upperStatus = rawStatus.toUpperCase();

          if (upperStatus.includes("HOLIDAY")) {
            if (!holidays.has(isoDate)) {
              holidays.set(isoDate, rawStatus);
            }
            continue;
          }

          const isAvailable = upperStatus === "YES";
          if (!dateAvailabilityMap[isoDate]) {
            dateAvailabilityMap[isoDate] = isAvailable;
          } else {
            dateAvailabilityMap[isoDate] = dateAvailabilityMap[isoDate] || isAvailable;
          }
        }

        for (const dateStr of futureDates) {
          if (holidays.has(dateStr)) {
            continue;
          } else if (naDates.includes(dateStr)) {
            unavailableDates.add(dateStr);
          } else if (dateAvailabilityMap[dateStr]) {
            availableDates.add(dateStr);
          } else {
            unavailableDates.add(dateStr);
          }
        }
      } else {
        for (const dateStr of futureDates) {
          unavailableDates.add(dateStr);
        }
      }

      setDatesWithDoctors(availableDates);
      setDatesWithoutDoctors(unavailableDates);
      setHolidayDates(holidays);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching weekly schedule:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDoctorWeeklySchedule();
    // eslint-disable-next-line
  }, [hospitalName]);

  // Calendar event mapping
  const events = allDatesInMonth.map((dateStr) => {
    if (holidayDates.has(dateStr)) {
      const holidayName = holidayDates.get(dateStr);
      return {
        title: "Holiday",
        start: dateStr,
        backgroundColor: "transparent",
        borderColor: "transparent",
        textColor: "white",
        extendedProps: {
          status: "holiday",
          label: holidayName,
          tooltip: `${holidayName} - Hospital Closed (Not Clickable)`,
        },
        className: "fc-disabled-date fc-holiday-date",
      };
    }

    if (naDates.includes(dateStr) || datesWithoutDoctors.has(dateStr)) {
      return {
        title: "N/A",
        start: dateStr,
        backgroundColor: "transparent",
        borderColor: "transparent",
        textColor: "white",
        extendedProps: {
          status: "na",
          tooltip: "No Doctors Available (Not Clickable)",
        },
        className: "fc-disabled-date fc-no-doctors",
      };
    }

    if (futureDates.includes(dateStr) && datesWithDoctors.has(dateStr)) {
      return {
        title: "A",
        start: dateStr,
        backgroundColor: "transparent",
        borderColor: "transparent",
        textColor: "white",
        extendedProps: {
          status: "available",
          tooltip: "Doctors Available! Click anywhere in this cell to book.",
        },
        className: "fc-available-date",
      };
    }

    return {
      title: "",
      start: dateStr,
      display: "none",
      extendedProps: { status: "disabled" },
      className: "fc-disabled-date",
    };
  });

  // Fetch doctors for a selected date
  const triggerDoctorFetch = async (dateStr) => {
    sessionStorage.setItem("SELECTED_DATE", dateStr);
    sessionStorage.removeItem("SELECTED_TIME");
    sessionStorage.removeItem("SELECTED_DATETIME");

    setModalLoading(true);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/GetSingleDayDoctorSchedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dept: sessionStorage.getItem("SELECTED_DEPARTMENT_ID") || "SS_01",
            date: dateStr,
            type: hospitalName === "hazra" ? "hazra" : "newtown",
          }),
        }
      );

      const result = await response.json();

      if (
        result.status === 200 &&
        result.data["doctor-details"] &&
        result.data["doctor-details"].length > 0
      ) {
        const mappedDoctors = result.data["doctor-details"].map((doc) => ({
          name: doc.DOCTOR_NAME || doc.DEPT_CODE || "-",
          from: doc.FROM_TIME || "-",
          to: doc.TO_TIME || "-",
          code: doc.DOCTOR_CODE,
          available: doc.AVAILABLE_STATUS === "YES",
        }));

        if (mappedDoctors.length > 0) {
          setModalData({
            date: dateStr,
            doctors: mappedDoctors,
          });
          setShowModal(true);
        } else {
          setModalData(null);
          toast.info("No doctors found for the selected date.", {
            position: "top-center",
            autoClose: 2000,
          });
        }
      } else {
        setModalData(null);
        toast.info("No doctors found for the selected date.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      setModalData(null);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Could not connect to the server. Please try again.",
      });
      console.error("API error:", error);
    } finally {
      setModalLoading(false);
    }
  };

  // Full cell click handler
  const handleDateClick = async (info) => {
    const dateStr = info.dateStr;

    if (holidayDates.has(dateStr)) {
      toast.info(`${holidayDates.get(dateStr)} - Hospital OPD closed!`, {
        autoClose: 2500,
        position: "top-center",
      });
      return;
    }

    if (
      naDates.includes(dateStr) ||
      datesWithoutDoctors.has(dateStr) ||
      !futureDates.includes(dateStr)
    ) {
      toast.error("No doctors are available on this date!", {
        autoClose: 2000,
        position: "top-center",
      });
      return;
    }

    if (futureDates.includes(dateStr) && datesWithDoctors.has(dateStr)) {
      await triggerDoctorFetch(dateStr);
    }
  };

  // Badge/Button click handler
  const handleEventClick = async (clickInfo) => {
    clickInfo.jsEvent.preventDefault();
    const dateStr = clickInfo.event.startStr;
    const status = clickInfo.event.extendedProps?.status;

    if (status === "holiday") {
      toast.info(`${holidayDates.get(dateStr)} - Hospital OPD closed!`, {
        autoClose: 2500,
        position: "top-center",
      });
      return;
    }

    if (status === "available") {
      await triggerDoctorFetch(dateStr);
    } else {
      toast.error("No doctors are available on this date!", {
        autoClose: 2000,
        position: "top-center",
      });
    }
  };

  useEffect(() => {
    if (
      modalData &&
      modalData.doctors &&
      modalData.doctors.length > 0 &&
      !modalData.doctors.some((doc) => doc.available)
    ) {
      toast.warning("No doctors are currently available for booking.", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [modalData]);

  const handleDoctorSelection = (doctor) => {
    const loginMobile = sessionStorage.getItem("loginMobile");
    if (doctor.available) {
      confirmAppointment(doctor);
      if (loginMobile) {
        setIsModalOpen(true);
      } else {
        navigate("/login");
      }
    } else {
      toast.error("Doctor is not available for booking!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const confirmAppointment = (doctor) => {
    setSelectedDate(modalData.date);
    setShowModal(false);

    sessionStorage.setItem("selectedDoctor", JSON.stringify(doctor));
    sessionStorage.setItem("selectedDoctorFromTime", doctor.from || "-");

    if (doctor?.code) {
      sessionStorage.setItem("selectedDoctorCode", doctor.code);
    }
  };

  const renderEventContent = (eventInfo) => {
    const status = eventInfo.event.extendedProps?.status;
    const label = eventInfo.event.extendedProps?.label;
    const tooltipText = eventInfo.event.extendedProps?.tooltip;

    if (status === "holiday") {
      const cleanName = label.includes("(") ? label.split("(")[1]?.replace(")", "") : label;
      return (
        <div className="cal-cell-badge cal-badge-holiday" title={tooltipText}>
          <span className="cal-badge-text">Holiday</span>
          {cleanName && <span className="cal-badge-subtext">{cleanName}</span>}
        </div>
      );
    }

    if (status === "na") {
      return (
        <div className="cal-cell-badge cal-badge-na" title={tooltipText}>
          <span className="cal-badge-text">N/A</span>
        </div>
      );
    }

    if (status === "available") {
      return (
        <div className="cal-cell-badge cal-badge-available" title={tooltipText}>
          <span className="cal-badge-text">A</span>
          <span className="cal-badge-subtext">Book Now</span>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <ToastContainer position="top-right" />

      {/* Screen Loader right after date or button is clicked */}
      <AnimatePresence>
        {modalLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(4px)",
            }}
          >
            <div className="bg-white px-5 py-4 rounded-3 shadow-lg d-flex flex-column align-items-center gap-3">
              <PulseLoader color="#2563eb" size={12} />
              <span className="fw-semibold text-secondary small">
                Fetching available doctors...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="card shadow border-0 bg-light">
        <div className="card shadow-sm rounded-4 border-0">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h4 className="text-success fw-bold">
              {hospitalName === "hazra"
                ? "Chittaranjan National Cancer Institute, Hazra, Kolkata"
                : "Chittaranjan National Cancer Institute, New Town, Kolkata"}
            </h4>

            <h5 className="text-primary fw-bold">
              <i className="fas fa-calendar-alt"></i>&nbsp;
              Select Date of Appointment
            </h5>
          </div>

          <div className="mt-3 d-flex ml-4 gap-4 align-items-center flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <div
                className="legend-col rounded-circle"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#10B981",
                }}
              ></div>
              <span className="small text-muted fw-semibold">Available (Click to book)</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div
                className="legend-color bg-danger rounded-circle"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#EF4444",
                }}
              ></div>
              <span className="small text-muted fw-semibold">Unavailable</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#6B7280",
                }}
              ></div>
              <span className="small text-muted fw-semibold">Holiday</span>
            </div>
          </div>

          <div className="card shadow-sm border-0 rounded-4 p-3 bg-white">
            <div className="card-body p-4">
              <h5 className="mb-4 fw-bold text-primary d-flex align-items-center">
                <i className="fa-solid fa-calendar-days me-2"></i> Appointment Calendar
              </h5>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center p-4 flex-wrap text-center">
                  <PulseLoader color="black" speedMultiplier={1} />
                  <span className="ms-3 mt-2 mt-sm-0">
                    Loading doctor availability...
                  </span>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <style jsx>{`
                    .fc {
                      font-family: 'Inter', sans-serif;
                    }
                    .fc-toolbar {
                      padding: 1rem;
                      background: #467FCF;
                      color: white;
                      border-radius: 0.75rem 0.75rem 0 0;
                    }
                    .fc-toolbar-title {
                      font-size: 1.25rem !important;
                      font-weight: 700 !important;
                      color: white !important;
                    }
                    .fc-button {
                      background: rgba(255,255,255,0.2) !important;
                      border: 1px solid rgba(255,255,255,0.3) !important;
                      color: white !important;
                      border-radius: 0.5rem !important;
                      padding: 0.5rem 1rem !important;
                      font-weight: 500 !important;
                      transition: all 0.3s ease !important;
                    }
                    .fc-button:hover {
                      background: rgba(255,255,255,0.3) !important;
                      transform: translateY(-1px);
                    }
                    .fc-button:disabled {
                      opacity: 0.5 !important;
                    }

                    /* Grid Cell Base */
                    .fc-theme-standard td, .fc-theme-standard th {
                      border-color: #e5e7eb !important;
                    }
                    .fc-daygrid-day-frame {
                      min-height: 5.6rem !important;
                      display: flex !important;
                      flex-direction: column !important;
                      justify-content: space-between !important;
                      padding: 4px !important;
                      box-sizing: border-box !important;
                    }
                    .fc-daygrid-day-top {
                      display: flex !important;
                      flex-direction: row !important;
                      justify-content: flex-end !important;
                      pointer-events: none; /* Passes click through to day cell */
                    }
                    .fc-daygrid-day-number {
                      padding: 0.25rem 0.4rem !important;
                      font-weight: 600;
                      color: #374151;
                      font-size: 0.85rem;
                      z-index: 2;
                    }
                    .fc-day-today .fc-daygrid-day-number {
                      background: #1d4ed8;
                      color: white;
                      border-radius: 50%;
                      width: 1.6rem;
                      height: 1.6rem;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      margin: 2px;
                    }

                    /* Full date cell interactive states */
                    .cell-available {
                      cursor: pointer !important;
                      transition: all 0.2s ease;
                    }
                    .cell-available:hover {
                      background-color: #ecfdf5 !important;
                    }
                    .cell-available:hover .cal-badge-available {
                      background: linear-gradient(135deg, #059669, #047857);
                      transform: scale(1.03);
                      box-shadow: 0 4px 10px rgba(16, 185, 129, 0.45);
                    }
                    .cell-disabled {
                      cursor: not-allowed !important;
                    }
                    .cell-disabled:hover {
                      background-color: #f9fafb !important;
                    }

                    /* Event container */
                    .fc-daygrid-day-events {
                      margin-top: auto !important;
                      margin-bottom: 2px !important;
                    }
                    .fc-event {
                      background: transparent !important;
                      border: none !important;
                      box-shadow: none !important;
                      padding: 0 !important;
                      margin: 0 !important;
                      display: block !important;
                      width: 100% !important;
                    }

                    /* Badges/Buttons */
                    .cal-cell-badge {
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      justify-content: center;
                      width: 100%;
                      padding: 5px 2px;
                      border-radius: 6px;
                      color: white;
                      text-align: center;
                      transition: all 0.2s ease-in-out;
                      user-select: none;
                    }
                    .cal-badge-text {
                      font-size: 0.85rem;
                      font-weight: 700;
                      line-height: 1.1;
                    }
                    .cal-badge-subtext {
                      font-size: 0.62rem;
                      font-weight: 600;
                      line-height: 1;
                      margin-top: 3px;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 95%;
                    }

                    /* Available Button Styling */
                    .cal-badge-available {
                      background: linear-gradient(135deg, #10b981, #047857);
                      cursor: pointer !important;
                      box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
                    }
                    .cal-badge-na {
                      background: linear-gradient(135deg, #ef4444, #dc2626);
                      cursor: not-allowed !important;
                      opacity: 0.9;
                    }
                    .cal-badge-holiday {
                      background: linear-gradient(135deg, #6b7280, #4b5563);
                      cursor: not-allowed !important;
                      opacity: 0.9;
                    }

                    @media (max-width: 640px) {
                      .fc-daygrid-day-frame {
                        min-height: 4.2rem !important;
                      }
                      .cal-badge-subtext {
                        display: none;
                      }
                    }
                  `}</style>

                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    weekends={true}
                    events={events}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    fixedWeekCount={false}
                    height="auto"
                    eventContent={renderEventContent}
                    headerToolbar={{
                      left: "prev",
                      center: "title",
                      right: "next",
                    }}
                    dayCellClassNames={(arg) => {
                      const dateStr = arg.date.toISOString().split("T")[0];
                      const classes = ["transition-all", "duration-200"];

                      if (arg.isToday) {
                        classes.push("bg-blue-50");
                      }

                      if (futureDates.includes(dateStr) && datesWithDoctors.has(dateStr)) {
                        classes.push("cell-available");
                      } else {
                        classes.push("cell-disabled");
                      }

                      return classes;
                    }}
                    dayCellDidMount={(info) => {
                      const dateStr = info.date.toISOString().split("T")[0];
                      if (holidayDates.has(dateStr)) {
                        info.el.setAttribute("title", `${holidayDates.get(dateStr)} - Hospital Closed`);
                      } else if (futureDates.includes(dateStr) && datesWithDoctors.has(dateStr)) {
                        info.el.setAttribute("title", "Click to select doctor and book appointment");
                      } else {
                        info.el.setAttribute("title", "Not available for booking");
                      }
                    }}
                  />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-4">
                <button
                  onClick={onPrevious}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-semibold rounded-md shadow hover:bg-gray-700 transition duration-150"
                >
                  <i className="fas fa-arrow-left"></i>
                  Previous
                </button>

                <button
                  onClick={onReset}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md shadow hover:bg-blue-700 transition duration-150"
                >
                  <i className="fas fa-rotate-right"></i>
                  Start Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {modalData && showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(8px)",
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-blue-500 text-white p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <i className="fa fa-calendar text-black text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-white">
                        Confirm Your Appointment
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {new Date(modalData.date).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-6 h-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors text-red-500"
                  >
                    <i className="fas fa-times text-lg"></i>
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">
                      Available Doctors
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Select a doctor to book your appointment
                    </p>
                  </div>

                  <div className="block sm:hidden space-y-4">
                    {modalData?.doctors?.length > 0 ? (
                      modalData.doctors.map((doctor, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            doctor.available
                              ? "border-emerald-200 bg-emerald-50 hover:border-emerald-300 hover:shadow-md"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div
                                className={`w-3 h-3 rounded-full ${
                                  doctor.available ? "bg-emerald-500" : "bg-gray-400"
                                }`}
                              ></div>
                              <h5 className="font-semibold text-gray-800">
                                Dr. {doctor.name}
                              </h5>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                doctor.available
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {doctor.available ? "Available" : "Unavailable"}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mb-4">
                            <div className="flex space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <i className="fas fa-clock text-emerald-500"></i>
                                <span>{doctor.from}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <i className="fas fa-clock text-red-500"></i>
                                <span>{doctor.to}</span>
                              </div>
                            </div>
                          </div>

                          <button
                            className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                              doctor.available
                                ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 transform hover:scale-105 shadow-lg"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                            }`}
                            disabled={!doctor.available}
                            onClick={() => handleDoctorSelection(doctor)}
                          >
                            {doctor.available ? (
                              <>
                                <i className="fas fa-check-circle"></i>
                                <span>Book Appointment</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-ban"></i>
                                <span>Not Available</span>
                              </>
                            )}
                          </button>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="fas fa-user-md text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-gray-600">
                          No doctors found for this date.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="hidden sm:block">
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200">
                              <div className="flex items-center space-x-2">
                                <i className="fas fa-user-md text-blue-500"></i>
                                <span>Doctor</span>
                              </div>
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200">
                              <div className="flex items-center space-x-2">
                                <i className="fas fa-clock text-emerald-500"></i>
                                <span>Start Time</span>
                              </div>
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200">
                              <div className="flex items-center space-x-2">
                                <i className="fas fa-clock text-red-500"></i>
                                <span>End Time</span>
                              </div>
                            </th>
                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                              <div className="flex items-center justify-center space-x-2">
                                <i className="fas fa-calendar-plus text-indigo-500"></i>
                                <span>Book Appointment</span>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {modalData?.doctors?.length > 0 ? (
                            modalData.doctors.map((doctor, index) => (
                              <tr
                                key={index}
                                className={`border-t border-gray-200 hover:bg-gray-50 transition-colors ${
                                  !doctor.available ? "opacity-60" : ""
                                }`}
                              >
                                <td className="px-6 py-4 border-r border-gray-200">
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`w-3 h-3 rounded-full ${
                                        doctor.available
                                          ? "bg-emerald-500"
                                          : "bg-gray-400"
                                      }`}
                                    ></div>
                                    <div>
                                      <div className="font-semibold text-gray-900">
                                        Dr. {doctor.name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {doctor.available
                                          ? "Available for booking"
                                          : "Currently unavailable"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 border-r border-gray-200">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                                    {doctor.from}
                                  </span>
                                </td>
                                <td className="px-6 py-4 border-r border-gray-200">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                    {doctor.to}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <button
                                    className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                                      doctor.available
                                        ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                                    }`}
                                    disabled={!doctor.available}
                                    onClick={() => handleDoctorSelection(doctor)}
                                  >
                                    {doctor.available ? (
                                      <>
                                        <i className="fas fa-check-circle"></i>
                                        <span>Book Now</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-ban"></i>
                                        <span>Unavailable</span>
                                      </>
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="px-6 py-12 text-center">
                                <div className="flex flex-col items-center space-y-4">
                                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                    <i className="fas fa-user-md text-2xl text-gray-400"></i>
                                  </div>
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                      No Doctors Available
                                    </h4>
                                    <p className="text-gray-500">
                                      No doctors found for the selected date.
                                      Please try another date.
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 transform hover:scale-105"
                >
                  <i className="fas fa-times"></i>
                  <span>Close</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {isModalOpen && <ExistingPatient doctor={doctor} onClose={closeModal} />}
    </>
  );
}

export default Calendar;